import React, { useEffect, useMemo, useRef, useState } from "react";
import Layout from "../../components/Layout";
import "../../styles/member/terms.css";
import { TermsService, TermsItem } from "../../services/termsService";
import { Eye, Pencil, Trash2 } from "lucide-react";
import ConfirmDelete from "../../components/ConfirmDelete";
import { formatDateTime } from "../../utils/dateFormatter";

const Terms: React.FC = () => {
  const [items, setItems] = useState<TermsItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [showForm, setShowForm] = useState<boolean>(false);
  const [viewItem, setViewItem] = useState<TermsItem | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const [form, setForm] = useState<{
    id?: number;
    title: string;
    content: string;
    version: string;
    isActive: boolean;
  }>({ title: "", content: "", version: "", isActive: false });
  const [formFile, setFormFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [existingFileName, setExistingFileName] = useState<string | null>(null);
  const [existingFileUrl, setExistingFileUrl] = useState<string | null>(null);

  const isEditing = useMemo(() => typeof form.id === "number", [form.id]);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await TermsService.listTerms();
      const base = TermsService.getBaseUrl();
      const withUrls = data.map((it) => {
        const url = it.filePath
          ? /^https?:\/\//i.test(it.filePath)
            ? it.filePath
            : `${base}/${it.filePath.replace(/^\/+/, "")}`
          : undefined;
        return { ...it, fileUrl: url } as TermsItem;
      });
      setItems(withUrls);
    } catch (e: any) {
      setError(e?.message || "Lỗi tải dữ liệu");
    } finally {
      setLoading(false);
    }
  };
  const getFileName = (path?: string): string => {
    if (!path) return "";
    try {
      const clean = path.split("?")[0];
      const parts = clean.split("/");
      return parts[parts.length - 1] || "";
    } catch {
      return "";
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const hasContent = (form.content || "").trim().length > 0;
      const payload = {
        title: form.title.trim(),
        content: hasContent ? (form.content || "").trim() : undefined,
        version: form.version.trim(),
        isActive: form.isActive,
      };
      if (!payload.title || !payload.version) {
        setError("Vui lòng nhập tiêu đề và phiên bản");
        setLoading(false);
        return;
      }
      if (!hasContent && !formFile && !isEditing) {
        setError("Vui lòng nhập nội dung hoặc chọn tệp đính kèm");
        setLoading(false);
        return;
      }
      let saved: TermsItem | null = null;
      if (isEditing && form.id) {
        saved = await TermsService.updateTerms(form.id, payload);
      } else {
        saved = await TermsService.createTerms(payload);
      }
      if (saved && formFile) {
        try {
          await TermsService.uploadTermsFile(saved.id, formFile);
        } catch (err: any) {
          // Nếu upload lỗi, vẫn tiếp tục nhưng hiển thị lỗi
          setError(err?.message || "Upload tệp thất bại");
        }
      }
      if (saved && !formFile && isEditing && form.id) {
        // Cho phép giữ tệp cũ nếu không chọn tệp mới → không làm gì
      }
      setForm({ title: "", content: "", version: "", isActive: false });
      setFormFile(null);
      setShowForm(false);
      await load();
    } catch (e: any) {
      setError(e?.message || "Lưu thất bại");
    } finally {
      setLoading(false);
    }
  };

  const onEdit = (item: TermsItem) => {
    setForm({
      id: item.id,
      title: item.title,
      content: (item as any).content ?? "",
      version: item.version,
      isActive: item.isActive,
    });
    setFormFile(null);
    // Lưu thông tin tệp hiện có (không thể prefill input file vì giới hạn trình duyệt)
    setExistingFileName(
      item.originalFileName || getFileName(item.filePath || item.fileUrl)
    );
    setExistingFileUrl(item.fileUrl || null);
    setShowForm(true);
  };

  const onDelete = (id: number) => {
    setDeleteId(id);
  };

  const confirmDelete = async () => {
    if (deleteId == null) return;
    setLoading(true);
    setError("");
    try {
      await TermsService.deleteTerms(deleteId);
      if (form.id === deleteId)
        setForm({ title: "", content: "", version: "", isActive: false });
      await load();
    } catch (e: any) {
      setError(e?.message || "Xóa thất bại");
    } finally {
      setLoading(false);
      setDeleteId(null);
    }
  };

  const onCancelEdit = () => {
    setForm({ title: "", content: "", version: "", isActive: false });
    setShowForm(false);
  };

  const onView = (item: TermsItem) => {
    setViewItem(item);
  };

  return (
    <Layout>
      <div className="terms-layout">
        <div className="terms-col">
          <div className="admin-logs-container">
            <div className="header-section terms-header-row">
              <h2 className="terms-h2">Quản lý điều khoản</h2>
              <button
                className="btn-primary"
                disabled={loading}
                onClick={() => {
                  setForm({
                    title: "",
                    content: "",
                    version: "",
                    isActive: false,
                  });
                  setShowForm(true);
                }}
              >
                Tạo điều khoản
              </button>
            </div>

            {error && <div className="terms-error">{error}</div>}

            {showForm && (
              <div className="modal-overlay terms-modal-overlay">
                <div className="terms-modal">
                  <h3 className="terms-modal-title">
                    {isEditing ? "Cập nhật điều khoản" : "Tạo điều khoản"}
                  </h3>
                  <form onSubmit={onSubmit}>
                    <div className="terms-form-grid">
                      <div>
                        <label>Tiêu đề</label>
                        <input
                          type="text"
                          value={form.title}
                          onChange={(e) =>
                            setForm((s) => ({ ...s, title: e.target.value }))
                          }
                          placeholder="Nhập tiêu đề"
                        />
                      </div>
                      <div>
                        <label>Phiên bản</label>
                        <input
                          type="text"
                          value={form.version}
                          onChange={(e) =>
                            setForm((s) => ({ ...s, version: e.target.value }))
                          }
                          placeholder="Ví dụ: v1.0"
                        />
                      </div>
                      <div className="terms-grid-full">
                        <label>Nội dung</label>
                        <textarea
                          value={form.content}
                          onChange={(e) =>
                            setForm((s) => ({ ...s, content: e.target.value }))
                          }
                          placeholder="Nhập nội dung điều khoản"
                          rows={10}
                        />
                      </div>
                      <div className="terms-grid-full">
                        <label>Tệp đính kèm (tuỳ chọn)</label>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.gif"
                          onChange={(e) =>
                            setFormFile(
                              e.target.files && e.target.files[0]
                                ? e.target.files[0]
                                : null
                            )
                          }
                          className="terms-file-input-hidden"
                        />
                        {existingFileName && !formFile && (
                          <div className="terms-current-file">
                            <span className="terms-current-file-label">
                              Tệp hiện có:
                            </span>
                            {existingFileUrl ? (
                              <a
                                className="terms-current-file-link"
                                href={existingFileUrl}
                                target="_blank"
                                rel="noreferrer"
                                title={existingFileName}
                              >
                                {existingFileName}
                              </a>
                            ) : (
                              <span
                                className="terms-current-file-name"
                                title={existingFileName}
                              >
                                {existingFileName}
                              </span>
                            )}
                          </div>
                        )}
                        <div
                          onDragOver={(e) => {
                            e.preventDefault();
                            setIsDragging(true);
                          }}
                          onDragLeave={() => setIsDragging(false)}
                          onDrop={(e) => {
                            e.preventDefault();
                            setIsDragging(false);
                            const f = e.dataTransfer?.files?.[0];
                            if (f) setFormFile(f);
                          }}
                          onClick={() => fileInputRef.current?.click()}
                          className={`terms-dropzone ${
                            isDragging ? "is-drag" : ""
                          }`}
                        >
                          <div className="terms-dropzone-icon">⇪</div>
                          <div className="terms-dropzone-title">
                            {formFile
                              ? formFile.name
                              : "Chọn tệp hoặc kéo thả vào đây"}
                          </div>
                          <div className="terms-dropzone-sub">
                            png, jpg, pdf, docx được chấp nhận
                          </div>
                        </div>
                        <small className="terms-dropzone-hint">
                          Tối đa 20MB
                        </small>
                      </div>
                      <div className="terms-inline-row">
                        <input
                          id="isActive"
                          type="checkbox"
                          checked={form.isActive}
                          onChange={(e) =>
                            setForm((s) => ({
                              ...s,
                              isActive: e.target.checked,
                            }))
                          }
                        />
                        <label htmlFor="isActive">Đặt là bản hiệu lực</label>
                      </div>
                    </div>

                    <div className="modal-actions">
                      <button
                        type="button"
                        onClick={onCancelEdit}
                        className="btn-cancel"
                      >
                        Hủy
                      </button>
                      <button
                        type="submit"
                        disabled={loading}
                        className="btn-primary"
                      >
                        {isEditing ? "Cập nhật" : "Tạo mới"}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            <div className="terms-table-card">
              <div className="terms-table-scroll">
                <table className="terms-table">
                  <thead>
                    <tr className="terms-table-head-row">
                      <th className="terms-th">ID</th>
                      <th className="terms-th">Tiêu đề</th>
                      <th className="terms-th">Phiên bản</th>
                      <th className="terms-th">Tệp</th>
                      <th className="terms-th">Hiệu lực</th>
                      <th className="terms-th">Ngày phát hành</th>
                      <th className="terms-th">Hành động</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it) => (
                      <tr key={it.id}>
                        <td className="terms-td">
                          TM{it.id.toString().padStart(3, "0")}
                        </td>
                        <td className="terms-td terms-td-title">
                          <div className="terms-title">{it.title}</div>
                          {!it.fileUrl && (
                            <div className="terms-ellipsis-2">{it.content}</div>
                          )}
                        </td>
                        <td className="terms-td">{it.version}</td>
                        <td className="terms-td">
                          {it.fileUrl
                            ? it.originalFileName ||
                              getFileName(it.filePath || it.fileUrl)
                            : "-"}
                        </td>
                        <td className="terms-td">
                          {it.isActive ? (
                            <span className="terms-badge terms-badge-active">
                              Đang hiệu lực
                            </span>
                          ) : (
                            <span className="terms-badge terms-badge-inactive">
                              Không hiệu lực
                            </span>
                          )}
                        </td>
                        <td className="terms-td">
                          {it.publishedAt
                            ? formatDateTime(it.publishedAt)
                            : "-"}
                        </td>
                        <td className="terms-td">
                          <div className="terms-actions">
                            <button
                              className="btn-view"
                              aria-label="Xem"
                              title="Xem"
                              onClick={() => onView(it)}
                            >
                              <Eye size={16} />
                            </button>
                            <button
                              className="btn-edit"
                              aria-label="Sửa"
                              title="Sửa"
                              onClick={() => onEdit(it)}
                            >
                              <Pencil size={16} />
                            </button>
                            <button
                              className="btn-danger"
                              aria-label="Xóa"
                              title="Xóa"
                              onClick={() => onDelete(it.id)}
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {items.length === 0 && !loading && (
                      <tr>
                        <td colSpan={6} className="terms-empty">
                          Chưa có điều khoản nào
                        </td>
                      </tr>
                    )}
                    {loading && (
                      <tr>
                        <td colSpan={6} className="terms-loading">
                          Đang tải...
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <ConfirmDelete
              isOpen={deleteId !== null}
              title="Xác nhận xóa điều khoản"
              message="Bạn có chắc chắn muốn xóa điều khoản này? Hành động này không thể hoàn tác."
              confirmText="Xóa"
              cancelText="Hủy"
              isSubmitting={loading}
              onConfirm={confirmDelete}
              onCancel={() => setDeleteId(null)}
            />
            {viewItem && (
              <div
                className="modal-overlay terms-view-overlay"
                onClick={() => setViewItem(null)}
              >
                <div
                  className="modal terms-view-modal"
                  onClick={(e) => e.stopPropagation()}
                >
                  <h3 className="terms-modal-title">Chi tiết điều khoản</h3>
                  <div className="meta-grid">
                    <div>
                      <label>ID</label>
                      <div className="value-box">
                        DK{viewItem.id.toString().padStart(3, "0")}
                      </div>
                    </div>
                    <div>
                      <label>Tiêu đề</label>
                      <div className="value-box">{viewItem.title}</div>
                    </div>
                    <div>
                      <label>Phiên bản</label>
                      <div className="value-box">{viewItem.version}</div>
                    </div>
                    <div>
                      <label>Hiệu lực</label>
                      <div className="value-box">
                        {viewItem.isActive ? "Đang hiệu lực" : "Không hiệu lực"}
                      </div>
                    </div>
                    <div>
                      <label>Ngày phát hành</label>
                      <div className="value-box">
                        {viewItem.publishedAt
                          ? formatDateTime(viewItem.publishedAt)
                          : "-"}
                      </div>
                    </div>
                    {!viewItem.fileUrl ? (
                      <div style={{ gridColumn: "1 / -1" }}>
                        <label>Nội dung</label>
                        <div className="content-box">{viewItem.content}</div>
                      </div>
                    ) : (
                      <div style={{ gridColumn: "1 / -1" }}>
                        <label>Tệp</label>
                        <div className="content-box" style={{ padding: 0 }}>
                          <iframe
                            title="Điều khoản"
                            src={viewItem.fileUrl}
                            style={{
                              width: "100%",
                              height: 480,
                              border: "none",
                            }}
                          />
                          <div style={{ padding: 8 }}>
                            <a
                              href={viewItem.fileUrl}
                              target="_blank"
                              rel="noreferrer"
                            >
                              Mở tệp trong tab mới
                            </a>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="modal-actions">
                    <button
                      className="btn terms-action-btn"
                      onClick={() => setViewItem(null)}
                    >
                      Đóng
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Terms;
